import React, { Component } from 'react';
import CollectionBrowser from "./../../components/collectionBrowser";
import LookBookQuoteContainer from "./../../components/lookBookQuoteContainer";
import CollectionHero from "./../../components/collectionHero";
import LookBookCenteredImage from "./../../components/lookBookCenteredImage";
import LookBookFullWidthImage from "./../../components/lookBookFullWidthImage";
import LookBookOnePhotoAndColor from "./../../components/lookBookOnePhotoAndColor";
import LookBookSideBySideImages from "./../../components/lookBookSideBySideImages";
import StoryFooter from "./../../components/storyFooter";
import ScrollFadeIn from "./../../components/scrollFadeIn";
import BannerFadeIn from "./../../components/bannerFadeIn";
import BackButton from './../../alpineComponents/backButton';
import Footer from './../../components/footer';
import {Helmet} from "react-helmet";

export default class Intro extends Component {
  render() {
    return (
      <div>
        <Helmet>
          <title>{'The Joys of Jet Lag Excerpt | Introduction Kat Medina'}</title>
          <meta name="description" content="The Joys of Jet Lag begins in the Andes at the start of Kathryn Medina's six-month solo trip to South America. The introduction briefly explains why she embarked on the journey to write this book–to provide you with the tools and insights that you can use to live your best life, one of wholehearted adventure and joy, no matter where you are." />
        </Helmet>
        <BackButton />
        <BannerFadeIn>
          <CollectionHero
            image={'/images/intro-hero.jpg'}
            subtitle={'Ecuador & Beyond'}
            title={'Introduction'}
            mobileOptimize={true}
          />
        </BannerFadeIn>
        <ScrollFadeIn>
          <LookBookOnePhotoAndColor
            backgroundColor="#16a5c4"
            image='/images/intro-malacatos.jpg'
            latlng={[-4.220060,-79.259720]}
            city="Malacatos, Ecuador"
          />
        </ScrollFadeIn>
        <div className="medium-padding">
          <div className="text-max-width-container">
            <ScrollFadeIn>
              <p className="tiny-padding">{'The tiny beat-up Datsun taxi sped through the winding mountain roads high in the Andes. The driver, blasting salsa music, barely let off the gas as he passed other drivers, oblivious to the “Do Not Pass” signs that dotted the mountainside. A quick two honks followed by making the sign of the cross over his body was sufficient luck to pass on the countless blind turns. Too bad I wasn’t Catholic; I had to rely solely on the hope of honks. On the righthand side of the road stood a steep mountain wall with the occasional remnants of a recent landslide, and on the left, a severe drop down a thousand-foot ravine, without a single guardrail to obstruct the view or save an unlucky car. The cool, crisp mountain air blended with the smell of burning trash from the tiny, corrugated metal shack homes we passed. I understood instantly how different my life was about to be.'}
              </p>
            </ScrollFadeIn>
            <ScrollFadeIn>
              <p className="tiny-padding">{'It was January 2012, and I had arrived at a small Ecuadorian airport about 30 minutes prior with a large suitcase, my trusty orange and brown North Face backpack, and the same nervous hopefulness that had guided me on this adventure: six months in South America as a solo 26-year-old female.'}
              </p>
            </ScrollFadeIn>
            <ScrollFadeIn>
              <p className="tiny-padding">{'I had always been passionate about traveling, and as a child I had dreamed not of walking down the aisle with prince charming, but of becoming an explorer of the world’s far-off places. Travel was equal to happiness for me. This trip to South America was one of my many solo adventures abroad. I was driven by a deep desire to feel the aliveness that comes from going to a place where I didn’t know a soul and perceiving life with refreshing novelty. It was during that trip to Ecuador that I first noticed the differences between who I was on my travels and who I was at home.'}
              </p>
            </ScrollFadeIn>
          </div>
        </div>
        <LookBookSideBySideImages
          images={[
            {
                image:"/images/tall-rectangle.png",
                backgroundImage:'/images/intro-graffiti.jpg',
                latlng:[-0.180653,-78.467834],
                city:"Quito, Ecuador"
            },{
                image:"/images/tall-rectangle.png",
                backgroundImage:'/images/intro-tishi.jpg',
                latlng:[-4.220060,-79.259720],
                city:"Malacatos, Ecuador"
            },{
                image:"/images/tall-rectangle.png",
                backgroundImage:'/images/intro-glass.jpg',
                latlng:[-0.180653,-78.467834],
                city:"Quito, Ecuador"
            }
          ]}
        />
        <div className="medium-padding">
          <div className="text-max-width-container">
            <ScrollFadeIn>
              <p className="tiny-padding">{'We all know that feeling—the easygoing joy that naturally arises on a trip. We watch the world around us with curiosity, awed by unfamiliarity. The unexpected setbacks that we inevitably encounter are viewed as part of the adventure. We can accept them with humor, knowing they will become the elements of a good story. We participate in the moment at hand and uplift it by being openhearted. We meet new people, try new things, and appreciate the differences rather than avoiding or condemning them.'}</p>
            </ScrollFadeIn>
            <ScrollFadeIn>
              <p className="tiny-padding">{'And then our trip ends, and we return home. We fall back into our routine, attempting to control the uncontrollable, seeing challenges as unfair personal attacks from life, and becoming blind to the wonders and gifts around us because we’ve grown so accustomed to having them.'}</p>
            </ScrollFadeIn>
            <ScrollFadeIn>
              <p className="tiny-padding">{'Basically, we go into asshole autopilot, settling for a life of mediocrity that’s occasionally sprinkled with the brief and fleeting joy from a recent vacation. Before we know it, life will have passed us by and we’re guaranteed to look back with defeated eyes of regret, wondering what if. What if we had actually listened to the voice within, desperately begging us to find genuine happiness in the now, to pursue that which fulfills us, instead of burying it out of fear or doubt?'}</p>
            </ScrollFadeIn>
          </div>
        </div>
        <ScrollFadeIn>
          <LookBookCenteredImage
            image='/images/intro-dog.jpg'
            latlng={[-4.0075945,-79.2433984]}
            city="Loja, Ecuador"
          />
        </ScrollFadeIn>
        <div className="medium-padding">
          <div className="text-max-width-container">
            <ScrollFadeIn>
              <p className="tiny-padding">{'Discomfort awakens our capacity for wonder. When we explore beyond the outer limits of our comfort zone, we can once again see life with fresh eyes. Our senses absorb the differences around us, requiring us to pay attention and be present. Adventure grips hold of our spirit, shaking the long-dormant part of our authentic self that was forced into hibernation by the monotony of our daily lives.'} </p>
            </ScrollFadeIn>
            <ScrollFadeIn>
              <p className="tiny-padding">{'My journey to South America woke up a part of me that could never again be contained—the wild, authentic piece of me that craved the raw beauty that is only possible when we feel we’re living fully.'}</p>
            </ScrollFadeIn>
            <ScrollFadeIn>
              <p className="tiny-padding">{'As my trip neared its end, I decided to make it my mission to bridge the gap between who I was when I traveled and who I was at home. I examined the values and beliefs that I effortlessly embodied on my travels to more than 30 countries and applied them to my daily life. I noticed that when I adopted a traveler’s mindset, there was an undeniable impact on the quality of my life. A simple shift in my thinking completely transformed the way I saw the world. I began to appreciate the journey as much as the destination. I began to live fully.'}</p>
            </ScrollFadeIn>
          </div>
        </div>
        <ScrollFadeIn>
          <LookBookCenteredImage
            image='/images/intro-family.jpg'
            latlng={[-4.0075945,-79.2433984]}
            city="Loja, Ecuador"
          />
        </ScrollFadeIn>
        <div className="medium-padding">
          <div className="text-max-width-container">
            <ScrollFadeIn>
              <p className="tiny-padding">{'The Joys of Jet Lag is a guide to telling your inner asshole to suck it so you can create more joy in the day-to-day. It explores the values that constitute a traveler’s mindset and provides you with the tools and insights for living your best life, one of wholehearted adventure and unbridled joy, no matter where you are.'}</p>
            </ScrollFadeIn>
            <ScrollFadeIn>
              <p className="tiny-padding">{'Life is happening all around you, just waiting for you to open up and take a step out of your daily routine and into the unknown. It is during those brief moments of trust that the magic happens. I hope this book helps you to see it.'}</p>
            </ScrollFadeIn>
          </div>
        </div>
        <ScrollFadeIn>
          <LookBookFullWidthImage
            image='/images/about-guatever.jpg'
            latlng={[14.556830,-90.733620]}
            city="Antigua, Guatemala"
          />
        </ScrollFadeIn>
        <StoryFooter story="/intro" />
        <Footer />
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/pages/intro/index.js